import { Dialog, Transition } from "@headlessui/react";
import { Fragment } from "react";

type OverbookingConfirmationVariant = "update_overbooking_request" | "delete_overbooking_request";

interface IOverbookingConfirmationModalProps {
  open: boolean;
  variant?: OverbookingConfirmationVariant;
  title?: string;
  message?: string;
  impactCount?: number | null;
  impactPrefix?: string;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  confirmText?: string;
  confirmButtonClassName?: string;
  cancelText?: string;
  confirmDisabled?: boolean;
  cancelDisabled?: boolean;
  disableClose?: boolean;
  panelMaxWidthClassName?: string;
}

const VARIANT_CONTENT: Record<
  OverbookingConfirmationVariant,
  {
    title: string;
    message: string;
    impactPrefix: string;
    confirmText: string;
    confirmButtonClassName: string;
  }
> = {
  update_overbooking_request: {
    title: "Update Overbooking Request",
    message: "Are you sure you want to proceed ahead with overbooking request update?",
    impactPrefix: "Updating this overbooking request will impact",
    confirmText: "Update",
    confirmButtonClassName:
      "inline-flex justify-center bg-black px-6 py-2 rounded-md w-full sm:w-auto font-semibold text-white text-xs disabled:opacity-50",
  },
  delete_overbooking_request: {
    title: "Delete Overbooking Request",
    message: "Are you sure you want to proceed ahead with overbooking request deletion?",
    impactPrefix: "Deleting this overbooking request will impact",
    confirmText: "Delete",
    confirmButtonClassName:
      "inline-flex justify-center bg-[#E11D48] px-6 py-2 rounded-md w-full sm:w-auto font-semibold text-white text-xs disabled:opacity-50",
  },
};

const OverbookingConfirmationModal: React.FC<IOverbookingConfirmationModalProps> = ({
  open,
  variant,
  title,
  message,
  impactCount = null,
  impactPrefix,
  onClose,
  onConfirm,
  confirmText,
  confirmButtonClassName,
  cancelText = "Cancel",
  confirmDisabled = false,
  cancelDisabled = false,
  disableClose = false,
  panelMaxWidthClassName = "sm:max-w-[40rem]",
}) => {
  const variantContent = variant ? VARIANT_CONTENT[variant] : null;

  const resolvedTitle = title ?? variantContent?.title ?? "Confirm Action";
  const resolvedMessage = message ?? variantContent?.message ?? "Are you sure you want to continue?";
  const resolvedImpactPrefix = impactPrefix ?? variantContent?.impactPrefix ?? "";
  const resolvedConfirmText = confirmText ?? variantContent?.confirmText ?? "Confirm";
  const resolvedConfirmButtonClassName =
    confirmButtonClassName ??
    variantContent?.confirmButtonClassName ??
    "inline-flex justify-center bg-black px-6 py-2 rounded-md w-full sm:w-auto font-semibold text-white text-xs disabled:opacity-50";

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="z-50 fixed inset-0 overflow-y-auto"
        onClose={() => {
          if (disableClose) return;
          onClose();
        }}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="sm:block flex justify-center items-end px-4 sm:p-0 pt-4 pb-20 min-h-screen text-center">
          <span className="sm:inline-block hidden sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <Dialog.Panel
              className={`inline-block align-bottom bg-white shadow-xl sm:my-8 rounded-2xl sm:w-full ${panelMaxWidthClassName} text-left transform transition-all overflow-visible sm:align-middle px-4 sm:p-6 pt-5 pb-4 sm:pb-4`}
            >
              <Dialog.Title as="h3" className="mb-1 font-medium text-[#000] text-sm">
                {resolvedTitle}
              </Dialog.Title>
              <hr className="mt-3" />
              <p className="mt-4 text-sm text-[#1F1F23]">
                {impactCount !== null && impactCount > 0 && resolvedImpactPrefix ? (
                  <>
                    {resolvedImpactPrefix} <span className="mx-1 inline-block font-semibold text-red-600">{impactCount}</span>{" "}
                    bookings.
                    <br />
                  </>
                ) : null}
                {resolvedMessage}
              </p>
              <div className="flex justify-end gap-4 mt-6">
                <button
                  type="button"
                  onClick={onClose}
                  disabled={cancelDisabled}
                  className="px-4 py-2 rounded-lg text-xs font-medium border border-gray-200 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {cancelText}
                </button>
                <button type="button" onClick={onConfirm} disabled={confirmDisabled} className={resolvedConfirmButtonClassName}>
                  {resolvedConfirmText}
                </button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default OverbookingConfirmationModal;
